import { useCallback } from 'react';
import {
  matchPath,
  useLocation,
  useNavigate,
  useParams,
  type Location,
} from 'react-router-dom';

import {
  ARTIFACT_IDS,
  getArtifactSingular,
  type ArtifactId,
  type OverlayKind,
} from '@/artifacts/navigation-map';
import { ROUTES, mapRoutes, shareRoutes } from '@/routes/routes';
import { useGeoMarkupStore } from '@/store';
import { selectPublicShareToken } from '@/store/selectors';

import {
  isOverlayPath,
  resolveBackgroundArtifact,
  type BackgroundLocationState,
} from './useBackgroundLocation';

/**
 * Returns the background location to store on `location.state` when
 * navigating into an overlay route. Falls back to `undefined` (→ refresh
 * fallback) when we are already inside an overlay and there is no stored
 * background.
 */
function deriveBackgroundLocation(location: Location): Location | undefined {
  const state = location.state as BackgroundLocationState | null;
  if (state?.backgroundLocation) return state.backgroundLocation;
  if (isOverlayPath(location.pathname)) return undefined;
  return location;
}

/** True when the current public page is the embed variant (`/embed/:token`). */
function isEmbedPath(pathname: string): boolean {
  return matchPath(ROUTES.EMBED, pathname) !== null;
}

/**
 * Generic artifact navigation hook.
 *
 * Exposes exactly two entry points:
 *
 * - `openArtifact(artifactId, kind, itemId?)` — pushes the corresponding
 *   overlay URL (`/map/<singular>/:itemId`, `/map/<singular>/:itemId/edit`,
 *   or `/map/<singular>/new`) and stores `backgroundLocation` on
 *   `location.state`. `useSyncOverlayWithUrl` then reflects the URL into
 *   the correct artifact slice.
 * - `closeOverlay()` — closes the currently active overlay. For edit
 *   routes it walks up to the parent detail route; for detail/new routes
 *   it falls back to the stored `backgroundLocation` (or the first
 *   registered artifact's background path as a refresh fallback).
 *
 * Usage:
 * ```tsx
 * const { openArtifact, closeOverlay } = useArtifactNavigation();
 * onClick={() => openArtifact('tasks', 'detail', task.id)}
 * ```
 */
export function useArtifactNavigation(): {
  openArtifact: (
    artifactId: ArtifactId,
    kind: OverlayKind,
    itemId?: string
  ) => void;
  closeOverlay: () => void;
} {
  const navigate = useNavigate();
  const location = useLocation();
  const { shortId } = useParams<{ shortId: string }>();
  // Public share-viewer mode: artifact clicks navigate to the public detail
  // URL (`/share/:token/:singular/:itemId`); edit / new are unavailable.
  const publicShareToken = useGeoMarkupStore(selectPublicShareToken);

  // `closeMapOverlay` still lives on `uiSlice` (list/kanban "+Add" flow
  // toggles a pre-form overlay that is distinct from artifact panel
  // visibility). Every other panel flag is cleared via slice actions.
  const closeMapOverlay = useGeoMarkupStore(
    (state) => state.ui.closeMapOverlay
  );

  /**
   * Preserves the current location's query string (e.g. `?view=list`) when
   * navigating to an overlay URL. Critical because `useMapViewMode` reads
   * `?view=` from the current URL — without this, opening an overlay would
   * drop the view preference and flip-flop the panel layout between map
   * and list/kanban on every open/close.
   */
  const withCurrentSearch = useCallback(
    (pathname: string) => ({ pathname, search: location.search }),
    [location.search]
  );

  const openArtifact = useCallback(
    (artifactId: ArtifactId, kind: OverlayKind, itemId?: string) => {
      if (publicShareToken) {
        if (kind !== 'detail' || !itemId) return;
        const publicSingular = getArtifactSingular(artifactId);
        const pathname = isEmbedPath(location.pathname)
          ? shareRoutes.embedDetail(publicShareToken, publicSingular, itemId)
          : shareRoutes.detail(publicShareToken, publicSingular, itemId);
        navigate(withCurrentSearch(pathname));
        return;
      }

      if (!shortId) return;
      const singular = getArtifactSingular(artifactId);

      let pathname: string;
      if (kind === 'detail') {
        if (!itemId) return;
        pathname = mapRoutes.detail(singular, shortId, itemId);
      } else if (kind === 'edit') {
        if (!itemId) return;
        pathname = mapRoutes.edit(singular, shortId, itemId);
      } else {
        pathname = mapRoutes.new(singular, shortId);
      }

      const backgroundLocation = deriveBackgroundLocation(location);
      navigate(withCurrentSearch(pathname), {
        state: { backgroundLocation } satisfies BackgroundLocationState,
      });
    },
    [navigate, location, shortId, withCurrentSearch, publicShareToken]
  );

  /**
   * Closes the currently active overlay.
   *
   * Semantics:
   * - **Edit routes** (`/map/<singular>/:itemId/edit`) close to their
   *   **parent detail** route (`/map/<singular>/:itemId`), preserving
   *   `location.state` so a subsequent close still falls through to the
   *   background. This is fix #3 (preserved from the pre-registry code).
   * - **Detail routes** and **new routes** close to the stored
   *   `backgroundLocation` (or the first registered artifact's background
   *   path as a refresh fallback).
   *
   * In addition to navigating, this also proactively clears every
   * artifact's form + details flags plus the `mapOverlay` flag so the UI
   * transitions immediately without waiting for `useSyncOverlayWithUrl`
   * to catch the URL change. This makes close flows robust to edge cases
   * (e.g. when the form was opened via the pre-form `mapOverlay` flow
   * from list/kanban view).
   */
  const closeOverlay = useCallback(() => {
    const pathname = location.pathname;

    if (publicShareToken) {
      navigate(
        withCurrentSearch(
          isEmbedPath(pathname)
            ? shareRoutes.embed(publicShareToken)
            : shareRoutes.share(publicShareToken)
        )
      );
      return;
    }

    // Edit → parent detail (not background). Close the form flag on the
    // matching artifact and leave `detailsIsOpen` alone —
    // `useSyncOverlayWithUrl` will (re)open it when the URL transitions
    // to the detail route.
    for (const artifactId of ARTIFACT_IDS) {
      const singular = getArtifactSingular(artifactId);
      const editMatch = matchPath(
        {
          path: `/w/:shortId/map/${singular}/:itemId/edit`,
          end: true,
        },
        pathname
      );
      if (
        editMatch?.params.shortId &&
        editMatch.params.itemId &&
        editMatch.params.itemId !== 'new'
      ) {
        const store = useGeoMarkupStore.getState();
        store[artifactId].closeForm();
        closeMapOverlay();
        navigate(
          withCurrentSearch(
            mapRoutes.detail(
              singular,
              editMatch.params.shortId,
              editMatch.params.itemId
            )
          ),
          { state: location.state }
        );
        return;
      }
    }

    // Detail or `/new` → close to background location. Proactively clear
    // every panel flag that might be open so the UI matches the URL
    // immediately. This is a safety net: the URL → store sync hook
    // normally handles this, but proactive cleanup avoids flicker and
    // covers corner cases where a flag was set outside the URL flow
    // (e.g. `mapOverlay.isOpen` opened by the list/kanban "+Add" button).
    const store = useGeoMarkupStore.getState();
    for (const artifactId of ARTIFACT_IDS) {
      const slice = store[artifactId];
      slice.closeForm();
      slice.closeDetails();
    }
    closeMapOverlay();

    const state = location.state as BackgroundLocationState | null;
    if (state?.backgroundLocation) {
      // Merge current location.search into the stored backgroundLocation's
      // pathname. `backgroundLocation.search` may be stale (e.g. user
      // changed `?view=` on the overlay after opening it) — the current
      // URL's search is the authoritative view preference.
      navigate({
        pathname: state.backgroundLocation.pathname,
        search: location.search,
        hash: state.backgroundLocation.hash,
      });
      return;
    }
    if (shortId) {
      // Fallback when there's no stored backgroundLocation (e.g. forms opened
      // via a store flag without a URL push, like obstruction/note "create
      // here"). Resolve the active tab from the current URL so close returns
      // to the tab the user is on — not a hardcoded artifact.
      const fallbackArtifactId = resolveBackgroundArtifact(location.pathname);
      navigate({
        pathname: mapRoutes.background(fallbackArtifactId, shortId),
        search: location.search,
      });
    }
  }, [
    navigate,
    location.pathname,
    location.search,
    location.state,
    shortId,
    withCurrentSearch,
    closeMapOverlay,
    publicShareToken,
  ]);

  return {
    openArtifact,
    closeOverlay,
  };
}
